import { useMutation, useQuery } from '@apollo/client/react';
import { Accordion, Box, Button, Card, Center, Heading, HStack, Icon, Presence, Separator, SimpleGrid, Spinner, Switch, Text, VStack, Wrap } from '@chakra-ui/react';
import { SEAT_CATEGORIES } from '@src/components/sco/Advisors/data/seatCategories';
import { SEAT_COMMITMENTS, SEAT_COMPENSATION_TYPES, SEAT_INDUSTRIES } from '@src/components/sco/Advisors/data/seatIndustries';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { CreateSeatPreview } from '@src/components/sco/Advisors/sections/CreateSeatPreview';
import { SkillPicker } from '@src/components/sco/Advisors/sections/advisorApplication/SkillPicker';
import { PRESET_TAG_CHIP_PROPS, PresetTagPicker } from '@src/components/sco/Advisors/shared/PresetTagPicker';
import { StepBlock } from '@src/components/sco/Advisors/shared/StepBlock';
import { CATEGORY_KEY_TO_NAME } from '@src/components/sco/Advisors/utils/categoryKeys';
import { buildSeatWhyText, formatSeatCommitmentNotes, parseSeatCommitmentsFromNotes, parseSeatDosFromWhyText } from '@src/components/sco/Advisors/utils/engagementMappers';
import { ComboboxTagInput } from '@src/components/sco/ComboboxTagInput';
import { CompanyIndustryComboboxInput } from '@src/components/sco/CompanyIndustryComboboxInput';
import { SelectableButtons } from '@src/components/sco/SelectableButtons';
import { ADVISOR_ENGAGEMENT_STATUS_SEARCHING } from '@src/constants/advisor-engagement-statuses';
import { UPSERT_ADVISOR_ENGAGEMENT } from '@src/gql/mutations/UpsertAdvisorEngagement';
import { CREATE_SEAT_ENGAGEMENT } from '@src/gql/queries/CreateSeatEngagement';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { LuSparkles } from 'react-icons/lu';
const STEP_COUNT = 5;

/** Unordered compare so SelectableButtons mount onChange does not wipe later steps. */
const sameStringSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) {
    return false;
  }
  const other = new Set(b);
  return a.every(value => other.has(value));
};
type CreateSeatSectionProps = {
  seatId?: string;
};
export const CreateSeatSection = ({
  seatId
}: CreateSeatSectionProps = {}) => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const isEditMode = !!seatId;
  // ADV-G1: store selection by category key, not display name
  const [categoryKey, setCategoryKey] = useState<string | null>(null);
  const [industries, setIndustries] = useState<string[]>([]);
  const [skills, setSkills] = useState<string[]>([]);
  const [dos, setDos] = useState<string[]>([]);
  const [commitments, setCommitments] = useState<string[]>([]);
  const [compensation, setCompensation] = useState<string[]>([]);
  const [notify, setNotify] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const engagementQuery = useQuery(CREATE_SEAT_ENGAGEMENT, {
    skip: !seatId,
    variables: {
      advisorEngagementId: seatId!
    },
    fetchPolicy: 'network-only'
  });
  const [upsertAdvisorEngagement, upsertMutation] = useMutation(UPSERT_ADVISOR_ENGAGEMENT);
  const category = SEAT_CATEGORIES.find(c => c.key === categoryKey) || null;
  const palette = category?.palette || 'purple';
  const canAdd = !!category && skills.length > 0 && dos.length > 0;
  const stepsCanContinue = [categoryKey !== null, industries.length > 0, skills.length > 0, dos.length > 0, commitments.length > 0];
  const completedStepsKey = [...completedSteps].sort((a, b) => a - b).join(',');
  const isStepUnlocked = (index: number) => {
    if (!hasStarted) {
      return false;
    }
    if (index === 0) {
      return true;
    }
    return completedSteps.has(index - 1);
  };
  const invalidateFromStep = (fromIndex: number) => {
    setCompletedSteps(prev => {
      const next = new Set(prev);
      for (let i = fromIndex; i < STEP_COUNT; i++) {
        next.delete(i);
      }
      return next;
    });
  };
  const pickCategory = (key: string) => {
    setCategoryKey(key);
    setSkills([]);
    setDos([]);
    invalidateFromStep(0);
  };
  const handleGetStarted = () => {
    setHasStarted(true);
    setActiveIndex(0);
  };
  const handleContinue = (index_0: number) => {
    setCompletedSteps(prev_0 => new Set(prev_0).add(index_0));
    if (index_0 < STEP_COUNT - 1) {
      setActiveIndex(index_0 + 1);
    } else {
      setActiveIndex(null);
    }
  };
  useEffect(() => {
    if (!seatId || prefilled || !engagementQuery.data?.advisorEngagement) {
      return;
    }
    const engagement = engagementQuery.data.advisorEngagement;
    const seatCategory = SEAT_CATEGORIES.find(c_1 => c_1.key === engagement.categoryKey) || SEAT_CATEGORIES.find(c_0 => c_0.name === (engagement.category?.title || CATEGORY_KEY_TO_NAME[engagement.categoryKey ?? ''] || null));
    if (seatCategory) {
      setCategoryKey(seatCategory.key);
    }
    setIndustries((engagement.industryKeys ?? []).filter((value): value is string => !!value));
    setSkills((engagement.skillTagTitles ?? []).filter((value_0): value_0 is string => !!value_0));
    setDos([...parseSeatDosFromWhyText(engagement.whyText, seatCategory?.dos ?? [])]);
    const commitmentKeys = (engagement.timeCommitmentTypeKeys ?? []).filter((value_1): value_1 is string => !!value_1);
    if (commitmentKeys.length > 0) {
      setCommitments(commitmentKeys);
    } else {
      setCommitments([...parseSeatCommitmentsFromNotes(engagement.timeCommitmentNotes, SEAT_COMMITMENTS)]);
    }
    setCompensation((engagement.compensationTypeKeys ?? []).filter((value_2): value_2 is string => !!value_2));
    setNotify(engagement.notifyMatches !== false);
    setHasStarted(true);
    setCompletedSteps(new Set([0, 1, 2, 3, 4]));
    setPrefilled(true);
  }, [seatId, prefilled, engagementQuery.data?.advisorEngagement]);
  useEffect(() => {
    if (!hasStarted || activeIndex === null) {
      return;
    }
    const completed = completedStepsKey ? completedStepsKey.split(',').map(value_3 => parseInt(value_3, 10)) : [];
    const hasCompleted = (index_1: number) => completed.includes(index_1);
    const unlocked = activeIndex === 0 || hasCompleted(activeIndex - 1);
    if (!unlocked) {
      let lastUnlocked: number | null = null;
      for (let i_0 = STEP_COUNT - 1; i_0 >= 0; i_0--) {
        if (i_0 === 0 || hasCompleted(i_0 - 1)) {
          lastUnlocked = i_0;
          break;
        }
      }
      setActiveIndex(lastUnlocked);
    }
  }, [hasStarted, activeIndex, completedStepsKey]);
  const handleSubmit = async () => {
    if (!category || !canAdd) {
      return;
    }
    if (!category.key) {
      setSubmitError('Could not map the selected category.');
      return;
    }
    setSubmitError(null);
    try {
      const result = await upsertAdvisorEngagement({
        variables: {
          input: {
            advisorEngagementId: seatId,
            categoryKey: category.key,
            industryKeys: industries,
            skillTagTitles: skills,
            whyText: buildSeatWhyText(category.role, dos),
            timeCommitmentNotes: formatSeatCommitmentNotes(commitments, SEAT_COMMITMENTS),
            timeCommitmentTypeKeys: commitments,
            compensationTypeKeys: compensation,
            notifyMatches: notify,
            statusKey: isEditMode ? undefined : ADVISOR_ENGAGEMENT_STATUS_SEARCHING
          }
        }
      });
      const saved = result.data?.upsertAdvisorEngagement?.advisorEngagement;
      const savedSeatId = saved?.advisorEngagementId || saved?.id || seatId;
      if (savedSeatId) {
        const seatHref = `${paths.seats}/${savedSeatId}`;
        await router.push(isEditMode ? seatHref : `${seatHref}?added=1`);
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Could not save this board seat.');
    }
  };
  if (seatId && engagementQuery.loading) {
    return <Center height={'full'} padding={8} width={'full'}>
        <Spinner size={'xl'} />
      </Center>;
  }
  const canEdit = engagementQuery.data?.advisorEngagement?.currentUserCanEdit !== false;
  if (seatId && engagementQuery.data?.advisorEngagement && !canEdit) {
    return <Center height={'full'} padding={8} width={'full'}>
        <VStack gap={3} maxWidth={'md'} textAlign={'center'}>
          <Heading size={'lg'}>This seat can&apos;t be edited</Heading>
          <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
            Team-managed and Wil seats are updated automatically.
          </Text>
        </VStack>
      </Center>;
  }
  return <Box height={'full'} overflowY={'auto'} width={'full'}>
      <SimpleGrid columns={{
      base: 1,
      lg: 2
    }} gap={0} minHeight={'full'}>
        {/* form */}
        <VStack align={'stretch'} bgColor={'colorPalette.container/50'} gap={4} padding={4}>
          <Presence unmountOnExit animationDuration={'moderate'} present={!hasStarted} animationName={{
          _open: 'fade-in',
          _closed: 'fade-out'
        }}>
            <Card.Root bgColor={'colorPalette.card/30'} borderColor={'colorPalette.border'} borderRadius={'2xl'} borderWidth={'1px'} size={'sm'} variant={'subtle'}>
              <Card.Body alignItems={'center'} gap={4} justifyContent={'center'} padding={8} textAlign={'center'}>
                <VStack gap={2}>
                  <Icon boxSize={8} color={'colorPalette.solid'}>
                    <LuSparkles />
                  </Icon>
                  <Heading size={'2xl'}>{isEditMode ? 'Edit board seat' : 'Create a board seat'}</Heading>
                </VStack>
                <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                  A board seat is a defined role on your advisory board. Tell us the area you need help with,
                  the kind of advisor you want, and how they'll contribute — we'll start matching candidates
                  the moment you're done.
                </Text>
                <Button marginTop={2} variant={'cta'} onClick={handleGetStarted}>
                  Let's create a board seat &rarr;
                </Button>
              </Card.Body>
            </Card.Root>
          </Presence>

          <Accordion.Root bgColor={'transparent'} value={activeIndex !== null ? [activeIndex.toString()] : []} onValueChange={({
          value: value_4
        }) => {
          if (!hasStarted) {
            return;
          }
          const newVal = value_4?.[0];
          if (newVal == null) {
            setActiveIndex(null);
            return;
          }
          const index_2 = parseInt(newVal, 10);
          if (isStepUnlocked(index_2)) {
            setActiveIndex(index_2);
          }
        }}>
            <StepBlock canContinue={stepsCanContinue[0]} disabled={!isStepUnlocked(0)} isActive={activeIndex === 0} n={1} title={'Choose a category'} value={'0'} onContinue={() => handleContinue(0)} onContinueText={'Next: Industry →'}>
              <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                Build one seat at a time. Pick the single area where you most need a human.
              </Text>
              <SimpleGrid columns={{
              base: 1,
              sm: 2
            }} gap={2}>
                {SEAT_CATEGORIES.map(cat => {
                const selected = cat.key === categoryKey;
                return <Button key={cat.key} borderColor={selected ? 'colorPalette.solid' : 'colorPalette.border/50'} colorPalette={cat.palette} height={'auto'} justifyContent={'flex-start'} paddingY={3} variant={selected ? 'subtle' : 'outline'} onClick={() => pickCategory(cat.key)}>
                      <Icon boxSize={4}>
                        <cat.icon />
                      </Icon>
                      <Text fontSize={'sm'} fontWeight={'medium'}>
                        {cat.name}
                      </Text>
                    </Button>;
              })}
              </SimpleGrid>
            </StepBlock>

            <StepBlock canContinue={stepsCanContinue[1]} disabled={!isStepUnlocked(1)} isActive={activeIndex === 1} n={2} title={'Industry experience'} value={'1'} onContinue={() => handleContinue(1)} onContinueText={'Next: Skills →'}>
              <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                Pick the industries an advisor for this seat should have worked in. Industry sharpens the
                match.
              </Text>
              <PresetTagPicker addedLabel={'Your added industries'} addHelperText={"Search the full industry list or type one that isn't shown."} addLabel={'Add another industry'} selected={industries} suggested={SEAT_INDUSTRIES} suggestedLabel={'Common for startup boards'} renderCombobox={({
              add
            }) => <CompanyIndustryComboboxInput comboboxTagInputProps={{
              allowAddingItems: true,
              allowNonMatchingItems: true,
              placeholder: 'Search industries…'
            }} onChange={items => {
              const last = items[items.length - 1];
              if (last?.title) {
                add(last.title);
              }
            }} />} onChange={next_0 => {
              if (sameStringSet(industries, next_0)) {
                return;
              }
              setIndustries(next_0);
              invalidateFromStep(1);
            }} />
            </StepBlock>

            <StepBlock canContinue={stepsCanContinue[2]} disabled={!isStepUnlocked(2)} isActive={activeIndex === 2} n={3} title={'Specific skills'} value={'2'} onContinue={() => handleContinue(2)} onContinueText={"Next: What they'll do →"}>
              <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                Select the skills that matter most for this seat.
              </Text>
              <SkillPicker disabled={!category} disabledText={'Pick a category first to see the skills that fit.'} selected={skills} suggested={category?.skills ?? []} onChange={next_1 => {
              if (sameStringSet(skills, next_1)) {
                return;
              }
              setSkills(next_1);
              invalidateFromStep(2);
            }} />
            </StepBlock>

            <StepBlock canContinue={stepsCanContinue[3]} disabled={!isStepUnlocked(3)} isActive={activeIndex === 3} n={4} title={"What you'd love them to do"} value={'3'} onContinue={() => handleContinue(3)} onContinueText={'Next: Time commitment →'}>
              <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                The concrete things you want from this advisor — the more specific, the better the match.
              </Text>
              <PresetTagPicker addedLabel={'Your added asks'} addHelperText={'Not listed? Add a specific ask in your own words.'} addLabel={'Add another ask'} disabled={!category} disabledText={'Pick a category first.'} selected={dos} suggested={category?.dos ?? []} suggestedLabel={'Suggested for this seat'} renderCombobox={({
              add: add_0
            }) => <ComboboxTagInput allowAddingItems allowNonMatchingItems hideItemsOnBlank items={[]} placeholder={'Add something else…'} onChange={items_0 => {
              const last_0 = items_0[items_0.length - 1];
              if (last_0?.title) {
                add_0(last_0.title);
              }
            }} />} onChange={next_2 => {
              if (sameStringSet(dos, next_2)) {
                return;
              }
              setDos(next_2);
              invalidateFromStep(3);
            }} />
            </StepBlock>

            <StepBlock canContinue={stepsCanContinue[4]} disabled={!isStepUnlocked(4)} isActive={activeIndex === 4} n={5} title={'Time commitment'} value={'4'} onContinue={() => handleContinue(4)} onContinueText={'Add board seat →'}>
              <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                How involved should this advisor be? Pick the levels that fit this seat.
              </Text>
              <Wrap gap={2}>
                <SelectableButtons options={SEAT_COMMITMENTS.map(option => ({
                key: option.key,
                title: option.label
              }))} selectableButtonProps={PRESET_TAG_CHIP_PROPS} value={commitments} onChange={(options: Array<{
                key: string;
              }>) => {
                const next_3 = options.map(option_0 => option_0.key);
                if (sameStringSet(commitments, next_3)) {
                  return;
                }
                setCommitments(next_3);
                invalidateFromStep(4);
              }} />
              </Wrap>
              <Text color={'colorPalette.fg/60'} fontSize={'xs'} marginTop={2}>
                Compensation preference for this seat (optional)
              </Text>
              <Wrap gap={2}>
                <SelectableButtons options={SEAT_COMPENSATION_TYPES.map(option_1 => ({
                key: option_1.key,
                title: option_1.label
              }))} selectableButtonProps={PRESET_TAG_CHIP_PROPS} value={compensation} onChange={(options_0: Array<{
                key: string;
              }>) => {
                const next_4 = options_0.map(option_2 => option_2.key);
                if (sameStringSet(compensation, next_4)) {
                  return;
                }
                setCompensation(next_4);
                invalidateFromStep(4);
              }} />
              </Wrap>
            </StepBlock>
          </Accordion.Root>

          {completedSteps.has(4) && <VStack align={'stretch'} gap={2}>
              <Separator marginY={2} />
              <HStack align={'center'} gap={2} justify={'start'}>
                <Switch.Root aria-label={'Notify potential matches'} checked={notify} colorPalette={'green'} size={'md'} onCheckedChange={({
              checked
            }) => setNotify(checked)}>
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
                <Text fontSize={'md'} fontWeight={'medium'}>
                  Notify potential matches
                </Text>
              </HStack>
              <Text color={'colorPalette.fg/50'} fontSize={'sm'}>
                {notify ? 'We notify Startups.com members who match this seat. Their responses reach you on the live Request.' : 'This seat stays private. Nobody gets notified; find advisors yourself anytime.'}
              </Text>
              {submitError && <Text color={'red.fg'} fontSize={'sm'}>
                  {submitError}
                </Text>}
              <Button disabled={!canAdd} loading={upsertMutation.loading} marginTop={2} variant={'cta'} onClick={handleSubmit}>
                {isEditMode ? 'Save board seat →' : 'Add board seat →'}
              </Button>
            </VStack>}
        </VStack>

        <CreateSeatPreview category={category} commitments={commitments} dos={dos} industries={industries} palette={palette} skills={skills} />
      </SimpleGrid>
    </Box>;
};